import { StoreHeader } from "./StoreHeader";
import { StoreFooter } from "./SiteFooter";
import { Link } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import { getCart, dismissCartNotices } from "../lib/cartApi";
import { PackageX, X } from "lucide-react";
import { useCustomer } from "./CustomerContext";

// perf: the cart drawer and Rhyte AI load as separate chunks, after the page has painted (or at once when asked for)
const CartDrawer = lazy(() => import("./CartDrawer").then((m) => ({ default: m.CartDrawer })));
const Assistant = lazy(() => import("./assistant/Assistant").then((m) => ({ default: m.Assistant })));

function useDeferredMount(event) {
  const [on, setOn] = useState(false);
  const [first, setFirst] = useState(null);
  useEffect(() => {
    if (on) return undefined;
    const now = (e) => { if (e?.detail) setFirst(e.detail); setOn(true); };
    window.addEventListener(event, now);
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1200));
    const go = () => idle(() => setOn(true), { timeout: 3000 });
    if (document.readyState === "complete") go(); else window.addEventListener("load", go, { once: true });
    return () => { window.removeEventListener(event, now); window.removeEventListener("load", go); };
  }, [event, on]);
  return [on, first];
}

function Deferred() {
  const [cart, firstCart] = useDeferredMount("cart-drawer-open");
  const [asst] = useDeferredMount("assistant-open");  // a pending open is picked up on mount (window.__rstAsstPending)
  return (
    <Suspense fallback={null}>
      {cart && <CartDrawer initial={firstCart} />}
      {asst && <Assistant />}
    </Suspense>
  );
}

export const StoreLayout = ({ children }) => {
  const [count, setCount] = useState(0);
  const [removed, setRemoved] = useState([]);
  const { customer, logout } = useCustomer();
  useEffect(() => {
    const load = () =>
      getCart()
        .then((c) => { setCount((c.lines || []).reduce((a, l) => a + l.requested_quantity, 0)); setRemoved(c.removed_items || []); })
        .catch(() => {});
    load();
    const ping = (e) => e.key === "rst_cart_ping" && load();  // T52: count stays right across tabs
    window.addEventListener("cart-updated", load);
    window.addEventListener("focus", load);
    window.addEventListener("storage", ping);
    return () => { window.removeEventListener("cart-updated", load); window.removeEventListener("focus", load); window.removeEventListener("storage", ping); };
  }, []);
  return (
    <div data-testid="store-shell" className="min-h-screen bg-[#f7f8fb] text-[var(--brand-primary)] font-sans flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:bg-white focus:px-4 focus:py-2 focus:rounded">Skip to content</a>
      <StoreHeader count={count} customer={customer} logout={logout} />
      {removed.length > 0 && (
        <div role="status" data-testid="cart-removed-notice" className="max-w-6xl mx-auto mt-4 px-5 w-full">
          <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <PackageX size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <b>Some items are discontinued or currently unavailable to order, so we've removed them from your cart.</b>
              <ul className="mt-1 list-disc pl-5" data-testid="cart-removed-items">{removed.map((n) => <li key={n}>{n}</li>)}</ul>
            </div>
            <button type="button" aria-label="Dismiss" data-testid="cart-removed-dismiss" className="rounded-full p-1 hover:bg-amber-100"
              onClick={() => dismissCartNotices().finally(() => setRemoved([]))}><X size={16} aria-hidden="true" /></button>
          </div>
        </div>
      )}
      <main id="main" className="flex-1 w-full min-h-[100dvh]">{/* T53: the footer starts below the fold (no load-time layout shift) */}{children}</main>
      <Deferred />
      <StoreFooter />
    </div>
  );
};

export const priceBlock = (inc, ex) =>
  inc ? (
    <div>
      <div className="text-lg font-bold text-[var(--brand-primary)]" data-testid="price-inc">${Number(inc).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-xs font-normal text-slate-500">inc GST</span></div>
      {/* T50: customer product prices are inc GST only (the GST breakdown stays on cart / checkout / order totals) */}
    </div>
  ) : (
    <div className="text-sm text-slate-500">Price on application</div>
  );
